import {Col, message, Row} from 'antd'
import React, {useContext, useEffect, useState} from 'react'
import {useRevalidator, useRouteLoaderData, useSubmit} from 'react-router-dom'
import {ElementTemplatesType, ElementTypeId} from '../../entities/elementType'
import {Project, TypedElement} from '../../entities/project'
import {CanvasToolManager, ToolType} from './toolLayers/CanvasToolManager'
import {EditorCreateElements, ModeType} from './editors/EditorCreateElements'
import {useIntl} from 'react-intl'
import {EditProjectContext} from '../editProject/EditProject'
import {instance as projectService} from '../../services/ProjectsService'
import './CanvasEditorForElementsStep.less'

export type CanvasEditorForElementsStepProps = {
  availableElementTypes: ElementTypeId[]
}

export function CanvasEditorForElementsStep(props: CanvasEditorForElementsStepProps) {
  const {project} = useRouteLoaderData('project') as {project: Project}
  const [tool, setTool] = useState<ToolType>(null)
  const [messageApi, contextHolder] = message.useMessage()
  const [currentMode, setCurrentMode] = useState<ModeType>('create')
  const [elementsList, setElementsList] = useState<TypedElement[]>(project.elements)
  const [currentElementIndex, setCurrentElementIndex] = useState<number | null>(null)
  const [currentElementName, setCurrentElementName] = useState<string>('')
  const [currentElementType, setCurrentElementType] = useState<ElementTypeId>(
    props.availableElementTypes[0]
  )
  const [transformerProps, setTransformerProps] = useState<any>(null)
  const [draggableElement, setDraggableElement] = useState<string>(null)

  useEffect(() => {
    setTransformerProps(null)
    setCurrentElementName('')
  }, [currentElementIndex])

  useEffect(() => {
    setElementsList(project.elements)
  }, [project])

  const intl = useIntl()

  const submit = useSubmit()

  const isUnsaved = project.elements !== elementsList

  const {setNextStepConfirmation} = useContext(EditProjectContext)

  const onSave = async () => {
    const result = await projectService.editProject({
      ...project,
      elements: elementsList,
    })
    if (result.success === false) {
      messageApi.open({
        type: 'error',
        content: result.error?.message,
      })
    } else {
      submit({method: 'post'})
    }
  }

  useEffect(() => {
    if (isUnsaved) {
      setNextStepConfirmation({
        message: intl.formatMessage({id: 'doYouWantToSaveTheProjectBeforeMovingOnToTheNextStep'}),
        onConfirm: onSave,
      })
    } else {
      setNextStepConfirmation(null)
    }
  }, [isUnsaved])

  const onCurrentElementUpdated = (updatedElement: TypedElement) => {
    setElementsList((list) =>
      list.map((element) => {
        if (element.elementId === currentElementIndex) {
          return {...element, ...updatedElement}
        } else {
          return element
        }
      })
    )
  }

  const onCurrentElementDeleted = (deletedElementId: number) => {
    setElementsList(elementsList.filter((item) => item.elementId !== deletedElementId))
    //submit({method: 'post'})
  }

  return (
    <div className="canvas-editor-step">
      {contextHolder}

      <div className="canvas-editor-step-panel">
        <EditorCreateElements
          onSave={onSave}
          project={project}
          onToolSelected={setTool}
          tool={tool}
          isUnsaved={isUnsaved}
          currentElementName={currentElementName}
          onCurrentElementNameChange={setCurrentElementName}
          selectedElementType={currentElementType}
          onElementTypeSelected={(newType: ElementTypeId) => {
            setCurrentElementType(newType)
            //setCurrentElementName('')
          }}
          availableElementTypes={props.availableElementTypes}
          elementsList={elementsList}
          onCurrentElementUpdated={onCurrentElementUpdated}
          currentElement={elementsList.find((item) => item.elementId === currentElementIndex)}
          onCurrentElementSelected={(element) => {
            const elementIndex = elementsList.findIndex((e) => element.elementId === e.elementId)
            if (elementIndex < 0) {
              setCurrentElementIndex(null)
            } else {
              setCurrentElementIndex(elementIndex)
            }
          }}
          onCurrentElementDeleted={onCurrentElementDeleted}
          currentMode={currentMode}
          onCurrentModeChange={setCurrentMode}
          transformerProps={transformerProps}
          onTransformerPropsChange={setTransformerProps}
          onDraggableElementChange={setDraggableElement}
        ></EditorCreateElements>
      </div>
      <div className="canvas-editor-step-canvas">
        <div className="canvas-editor-step-canvas-frame">
          <CanvasToolManager
            canvasEditorProps={{
              projectId: project.id,
              plotShape: project.plotShape,
              elements: elementsList,
              plotShapeEditable: false,
              currentMode: currentMode,
              editableTypes: props.availableElementTypes,
              selectedElementId: currentElementIndex,
              setSelectedElementId: setCurrentElementIndex,
              selectable: false,
              hasMap: Boolean(project?.location?.map),
              onElementChanged: (updatedElement: TypedElement) => {
                setElementsList((list) =>
                  list.map((element) => {
                    if (element.elementId === updatedElement.elementId) {
                      return {...element, ...updatedElement}
                    } else {
                      return element
                    }
                  })
                )
              },
              transformerLayerProps: transformerProps,
              activeDraggableElement: {
                elementTypeId: currentElementType,
                temaplateId: draggableElement,
              },
              onDraggableElementChange: setDraggableElement,
              onDragEnd: () => {},
            }}
            tool={tool}
            onElementDrawn={(shape) => {
              setElementsList((elementsList) => [
                ...elementsList,
                {
                  type: currentElementType,
                  shapeType: shape.type,
                  props: shape.props,
                  elementId: Date.now(),
                  name:
                    currentElementName === ''
                      ? intl.formatMessage({id: `elementName-${currentElementType}`})
                      : currentElementName,
                  template: draggableElement,
                },
              ])
              setTool(null)
              // setCurrentElementName(
              //   currentElementName === ''
              //     ? intl.formatMessage({id: `elementName-${currentElementType}`})
              //     : currentElementName
              // )
            }}
          />
        </div>
      </div>
    </div>
  )
}
